/**
 * Notification drawer.
 *
 * One panel replaces three: `renderNotifications` (app.js),
 * `renderPatientNotifications` (patient_dashboard.js) and
 * `renderDoctorNotifications` (doctor_dashboard.js), which rendered the same
 * list into three different containers with three different mark-as-read rules.
 */

import { useNavigate } from 'react-router-dom';
import { Icon, EmptyState } from '../common';
import { useNotifications } from '../../context/NotificationContext';
import { useClickOutside } from '../../hooks/useLocalStorage';
import { actionToRoute, notificationIcon, notificationStyle } from '../../domain/notifications';

/** Relative time in Arabic, without pulling in a date library. */
function relativeTime(isoString) {
  const then = new Date(isoString).getTime();
  if (Number.isNaN(then)) return '';

  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return 'الآن';
  if (seconds < 3600) return `منذ ${Math.floor(seconds / 60)} دقيقة`;
  if (seconds < 86400) return `منذ ${Math.floor(seconds / 3600)} ساعة`;
  if (seconds < 604800) return `منذ ${Math.floor(seconds / 86400)} يوم`;
  return new Date(isoString).toLocaleDateString('ar-EG');
}

export function NotificationPanel() {
  const { notifications, setPanelOpen, markAsRead, markAllAsRead, remove, clearAll, unread } =
    useNotifications();
  const navigate = useNavigate();
  const panelRef = useClickOutside(() => setPanelOpen(false));

  const handleClick = (notification) => {
    markAsRead(notification.id);
    const route = actionToRoute(notification.action);
    if (route) {
      navigate(route);
      setPanelOpen(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[250] bg-black/40" aria-hidden={false}>
      <aside
        ref={panelRef}
        className="glass-panel absolute left-0 top-0 flex h-full w-full max-w-sm flex-col border-l border-white/10"
        role="dialog"
        aria-label="الإشعارات"
      >
        <header className="flex shrink-0 items-center justify-between border-b border-white/10 p-4">
          <h2 className="flex items-center gap-2 text-base font-bold text-white">
            <Icon name="bell" size={18} className="text-cyan-400" />
            الإشعارات
            {unread > 0 && (
              <span className="rounded-full bg-red-500 px-2 py-0.5 text-[10px] text-white">
                {unread}
              </span>
            )}
          </h2>
          <button
            type="button"
            onClick={() => setPanelOpen(false)}
            className="text-slate-400 transition-colors hover:text-white"
            aria-label="إغلاق"
          >
            <Icon name="x" size={20} />
          </button>
        </header>

        {notifications.length > 0 && (
          <div className="flex shrink-0 gap-2 border-b border-white/5 p-3">
            <button
              type="button"
              onClick={markAllAsRead}
              className="flex-1 rounded-lg bg-white/5 py-1.5 text-xs font-bold text-slate-300 hover:bg-white/10"
            >
              تحديد الكل كمقروء
            </button>
            <button
              type="button"
              onClick={clearAll}
              className="flex-1 rounded-lg bg-red-500/10 py-1.5 text-xs font-bold text-red-300 hover:bg-red-500/20"
            >
              حذف الكل
            </button>
          </div>
        )}

        <div className="custom-scrollbar flex-1 overflow-y-auto p-3">
          {notifications.length === 0 ? (
            <EmptyState
              icon="bell-off"
              title="لا توجد إشعارات"
              message="سيظهر هنا كل جديد يخص حجوزاتك ومواعيدك."
            />
          ) : (
            <ul className="space-y-2">
              {notifications.map((notification) => (
                <li key={notification.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleClick(notification)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') handleClick(notification);
                    }}
                    className={`flex cursor-pointer gap-3 rounded-xl border p-3 transition-colors hover:bg-white/5 ${
                      notification.read ? 'border-white/5 bg-white/[0.02]' : 'border-cyan-500/20 bg-cyan-500/5'
                    }`}
                  >
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${notificationStyle(
                        notification.type,
                      )}`}
                    >
                      <Icon name={notification.icon || notificationIcon(notification.type)} size={15} />
                    </span>

                    <div className="min-w-0 flex-1">
                      {notification.title && (
                        <p className="truncate text-sm font-bold text-white">{notification.title}</p>
                      )}
                      <p className="text-xs leading-relaxed text-slate-300">{notification.message}</p>
                      <span className="mt-1 block text-[10px] text-slate-500">
                        {relativeTime(notification.createdAt)}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        remove(notification.id);
                      }}
                      className="shrink-0 self-start text-slate-500 transition-colors hover:text-red-400"
                      aria-label="حذف الإشعار"
                    >
                      <Icon name="x" size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}
